import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Plus, Briefcase, Users, Calendar, DollarSign, FileText, Clock, X, CheckCircle, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper function to extract error message properly
const getErrorMessage = (error) => {
  if (error.response?.data) {
    const data = error.response.data;
    // Handle different error formats
    if (typeof data === 'string') return data;
    if (data.detail) {
      if (typeof data.detail === 'string') return data.detail;
      if (Array.isArray(data.detail)) {
        return data.detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
      }
      return JSON.stringify(data.detail);
    }
    if (data.message) return data.message;
    if (data.error) return data.error;
    return JSON.stringify(data);
  }
  return error.message || 'An unexpected error occurred';
};

function JobPostings({ user, onLogout }) {
  const [jobs, setJobs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [filter, setFilter] = useState({ status: '', employee_level: '' });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  // Master Data
  const [masterData, setMasterData] = useState({
    departments: [],
    locations: [],
    designations: [],
    employee_levels: []
  });

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
  };

  const [formData, setFormData] = useState({
    title: '',
    department: '',
    location: '',
    employee_level: 'Mid-Level',
    job_type: 'Full-time',
    experience_required: '0-1 years',
    salary_range_min: 0,
    salary_range_max: 0,
    description: '',
    requirements: [],
    responsibilities: [],
    skills_required: [],
    openings: 1,
    application_deadline: '',
    status: 'Active'
  });

  useEffect(() => {
    fetchMasterData();
    fetchJobs();
  }, []);

  const fetchMasterData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/master-data/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMasterData(response.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.employee_level) params.employee_level = filter.employee_level;
      
      const response = await axios.get(`${API}/api/recruitment/jobs`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingJob) {
        await axios.put(`${API}/api/recruitment/jobs/${editingJob.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast('Job updated successfully!', 'success');
      } else {
        await axios.post(`${API}/api/recruitment/jobs`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast('Job posted successfully!', 'success');
      }
      
      setShowForm(false);
      setEditingJob(null);
      fetchJobs();
      resetForm();
    } catch (error) {
      console.error('Job submission error:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setFormData({
      title: job.title || '',
      department: job.department || '',
      location: job.location || '',
      employee_level: job.employee_level || 'Mid-Level',
      job_type: job.job_type || 'Full-time',
      experience_required: job.experience_required || '0-1 years',
      salary_range_min: job.salary_range_min || 0,
      salary_range_max: job.salary_range_max || 0,
      description: job.description || '',
      requirements: job.requirements || [],
      responsibilities: job.responsibilities || [],
      skills_required: job.skills_required || [],
      openings: job.openings || 1,
      application_deadline: job.application_deadline || '',
      status: job.status || 'Active'
    });
    setShowForm(true);
  };

  const handleDelete = async (job) => {
    if (!window.confirm(`Delete job posting "${job.title}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/recruitment/jobs/${job.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Job deleted successfully!', 'success');
      fetchJobs();
    } catch (error) {
      console.error('Job deletion error:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const handleStatusChange = async (job, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/api/recruitment/jobs/${job.id}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast(`Job status updated to ${newStatus}`, 'success');
      fetchJobs();
    } catch (error) {
      console.error('Status change error:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '', department: '', location: '', employee_level: 'Mid-Level', job_type: 'Full-time',
      experience_required: '0-1 years', salary_range_min: 0, salary_range_max: 0, description: '',
      requirements: [], responsibilities: [], skills_required: [], openings: 1, application_deadline: '', status: 'Active'
    });
    setEditingJob(null);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-6 py-4 rounded-lg shadow-lg transition-all duration-300 ${
          toast.type === 'error' 
            ? 'bg-red-50 border-l-4 border-red-500 text-red-800' 
            : 'bg-green-50 border-l-4 border-green-500 text-green-800'
        }`}>
          {toast.type === 'error' ? (
            <AlertCircle className="w-6 h-6 text-red-500" />
          ) : (
            <CheckCircle className="w-6 h-6 text-green-500" />
          )}
          <div className="flex-1">
            <p className={`font-semibold ${toast.type === 'error' ? 'text-red-800' : 'text-green-800'}`}>
              {toast.type === 'error' ? 'Error' : 'Success'}
            </p>
            <p className={`text-sm ${toast.type === 'error' ? 'text-red-700' : 'text-green-700'}`}>
              {toast.message}
            </p>
          </div>
          <button 
            onClick={() => setToast({ show: false, message: '', type: 'success' })}
            className={`p-1 rounded-full hover:bg-opacity-20 ${
              toast.type === 'error' ? 'hover:bg-red-500' : 'hover:bg-green-500'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Job Postings</h1>
            <p className="text-gray-600 mt-2">Manage job openings and recruitment</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="px-6 py-3 text-white rounded-lg font-medium flex items-center gap-2"
            style={{backgroundColor: '#2b3380'}}
          >
            <Plus className="w-5 h-5" />
            Post New Job
          </button>
        </div>

        {/* Stats Cards - Clean white design */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#e8eaf6'}}>
                <Briefcase className="w-6 h-6" style={{color: '#2b3380'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#2b3380'}}>{jobs.filter(j => j.status === 'Open' || j.status === 'Active').length}</p>
                <p className="text-sm text-gray-500">Active Jobs</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#e8f5e9'}}>
                <Users className="w-6 h-6" style={{color: '#4caf50'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#4caf50'}}>{jobs.reduce((sum, j) => sum + (j.positions || j.openings || 0), 0)}</p>
                <p className="text-sm text-gray-500">Total Openings</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#fff3e0'}}>
                <FileText className="w-6 h-6" style={{color: '#ff9800'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#ff9800'}}>0</p>
                <p className="text-sm text-gray-500">Applications</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#e0f7fa'}}>
                <Clock className="w-6 h-6" style={{color: '#00bcd4'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#00bcd4'}}>{jobs.filter(j => new Date(j.created_at || j.posted_date) > new Date(Date.now() - 60*24*60*60*1000)).length}</p>
                <p className="text-sm text-gray-500">Recent (60 days)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Job Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl my-8 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-4" style={{color: '#2b3380'}}>Post New Job</h2>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Job Title *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Department *</label>
                    <select
                      value={formData.department}
                      onChange={(e) => setFormData({...formData, department: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    >
                      <option value="">Select Department</option>
                      {masterData.departments.map(dept => (
                        <option key={dept.id} value={dept.name}>{dept.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Location *</label>
                    <select
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    >
                      <option value="">Select Location</option>
                      {masterData.locations.map(loc => (
                        <option key={loc.id} value={loc.name}>{loc.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Employee Level *</label>
                    <select
                      value={formData.employee_level}
                      onChange={(e) => setFormData({...formData, employee_level: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    >
                      {masterData.employee_levels.map(level => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Job Type *</label>
                    <select
                      value={formData.job_type}
                      onChange={(e) => setFormData({...formData, job_type: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option>Full-time</option>
                      <option>Part-time</option>
                      <option>Contract</option>
                      <option>Intern</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Experience Required</label>
                    <select
                      value={formData.experience_required}
                      onChange={(e) => setFormData({...formData, experience_required: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="0-1 years">0-1 years</option>
                      <option value="1-3 years">1-3 years</option>
                      <option value="3-5 years">3-5 years</option>
                      <option value="5-7 years">5-7 years</option>
                      <option value="7-10 years">7-10 years</option>
                      <option value="10+ years">10+ years</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Salary Min (₹)</label>
                    <input
                      type="number"
                      value={formData.salary_range_min}
                      onChange={(e) => setFormData({...formData, salary_range_min: parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Salary Max (₹)</label>
                    <input
                      type="number"
                      value={formData.salary_range_max}
                      onChange={(e) => setFormData({...formData, salary_range_max: parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Number of Openings</label>
                    <input
                      type="number"
                      value={formData.openings}
                      onChange={(e) => setFormData({...formData, openings: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Application Deadline</label>
                    <input
                      type="date"
                      value={formData.application_deadline}
                      onChange={(e) => setFormData({...formData, application_deadline: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">Job Description *</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      rows="4"
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">Requirements (one per line)</label>
                    <textarea
                      value={Array.isArray(formData.requirements) ? formData.requirements.join('\n') : formData.requirements}
                      onChange={(e) => setFormData({...formData, requirements: e.target.value.split('\n').filter(r => r.trim())})}
                      rows="3"
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Enter each requirement on a new line"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">Skills Required</label>
                    <input
                      type="text"
                      value={formData.skills_required.join(', ')}
                      onChange={(e) => setFormData({...formData, skills_required: e.target.value.split(',').map(s => s.trim()).filter(s => s)})}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Comma-separated skills"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="submit" className="px-6 py-2 text-white rounded-lg" style={{backgroundColor: '#2b3380'}}>Post Job</button>
                  <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg">Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Jobs List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {jobs.map(job => (
            <div key={job.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-2 mb-3">
                <Briefcase className="w-5 h-5" style={{color: '#2b3380'}} />
                <h3 className="font-bold text-lg">{job.title}</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">{job.department} • {job.location}</p>
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4 text-gray-400" />
                  <span>{job.positions || job.openings || 0} {(job.positions || job.openings) === 1 ? 'position' : 'positions'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                  <span>₹{job.salary_range_min?.toLocaleString() || '-'} - ₹{job.salary_range_max?.toLocaleString() || '-'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span>{job.experience_required} exp</span>
                </div>
              </div>
              <span className="px-3 py-1 rounded-full text-sm font-medium text-white" style={{backgroundColor: (job.status === 'Active' || job.status === 'Open') ? '#4caf50' : '#757575'}}>
                {job.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}

export default JobPostings;